import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  Bot,
  Check,
  FileText,
  Video,
  Zap,
  TrendingUp,
  Target,
  ChevronDown,
  ExternalLink
} from 'lucide-react';
import {
  NetworkNode,
  TechChip,
  FinanceBuilding,
  GrowthCurve,
  GlobalSphere,
  StateBadge,
  StartupSeed,
  ServiceFlow,
  RetailTag
} from '../components/CompanyIcons';
import AnimatedHero from '../components/common/AnimatedHero';
import { useLocation } from 'react-router-dom';
import SEO from '../components/common/SEO';
import Footer from '../components/common/Footer';
import resumeTemplatesApi from '../services/resumeTemplatesApi';

// 自定义Hook：数字动画
const useCountAnimation = (targetValue, duration = 2000, isVisible = false) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    let animationId;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);

      // 使用缓动函数让动画更自然
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = Math.floor(easeOutQuart * targetValue);

      setCount(currentValue);

      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      } else {
        setCount(targetValue);
      }
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [targetValue, duration, isVisible]);

  return count;
};

// 统计项组件
const AnimatedStatItem = ({ number, label, suffix = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  const animatedCount = useCountAnimation(number, 2000, isVisible);

  useEffect(() => {
    const currentElement = elementRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [isVisible]);

  const formatNumber = (num) => {
    if (num >= 10000) {
      return `${Math.floor(num / 1000).toLocaleString()},000`;
    }
    return num.toLocaleString();
  };

  return (
    <StatItem ref={elementRef}>
      <StatNumber>
        {formatNumber(animatedCount)}{suffix}
      </StatNumber>
      <StatLabel>{label}</StatLabel>
    </StatItem>
  );
};

const HomePage = ({ onLoginClick }) => {
  const companiesSectionRef = useRef(null);
  const companiesFirstGroupRef = useRef(null);
  const [isCompaniesVisible, setIsCompaniesVisible] = useState(false);
  const [companiesScrollDistance, setCompaniesScrollDistance] = useState(0);
  const [activeResumeTemplateIndex, setActiveResumeTemplateIndex] = useState(1);
  const [isResumePreviewHovered, setIsResumePreviewHovered] = useState(false);
  const [realResumeTemplates, setRealResumeTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      setIsMobile(false);
      return;
    }

    const mediaQueryList = window.matchMedia('(max-width: 768px)');

    const update = () => {
      setIsMobile(mediaQueryList.matches);
    };

    update();

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', update);
      return () => mediaQueryList.removeEventListener('change', update);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 检测URL参数中的邀请码，自动打开登录模态框
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const inviteCode = urlParams.get('inviteCode');

    if (inviteCode && onLoginClick) {
      // 延迟一点时间，确保页面完全加载后再打开模态框
      const timer = setTimeout(() => {
        onLoginClick();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [location.search, onLoginClick]);

  // 使用Intersection Observer来检测轮播栏是否可见
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsCompaniesVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    const target = companiesSectionRef.current;
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, []);

  const handleGetStarted = () => {
    if (onLoginClick) {
      onLoginClick();
    }
  };

  // 规范化预览图路径（处理本地路径和OSS URL）
  const normalizePreviewPath = useCallback((path) => {
    if (!path) return '';
    const raw = String(path).replace(/^\//, '');

    // 如果是完整URL（OSS或CDN），直接返回
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      return raw;
    }

    // 如果是前端静态资源路径（/images/ 开头），直接返回，不添加 /assets/ 前缀
    if (raw.startsWith('images/') || path.startsWith('/images/')) {
      return path.startsWith('/') ? path : `/${raw}`;
    }

    // 本地路径处理：开发环境需要指向后端端口
    const protocol = typeof window !== 'undefined' ? window.location.protocol : 'http:';
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    const apiPort = process.env.REACT_APP_API_PORT || '8000';
    const base = process.env.NODE_ENV === 'production' ? '' : `${protocol}//${hostname}:${apiPort}`;

    if (raw.startsWith('assets/')) {
      return `${base}/${raw}`;
    }
    return `${base}/assets/${raw}`;
  }, []);

  const resumePreviewLineWidths = useMemo(
    () => [92, 78, 86, 70, 90, 76, 84, 68, 88, 74],
    []
  );

  const renderResumePreviewLines = (count) =>
    Array.from({ length: count }).map((_, index) => (
      <ResumePreviewLine
        key={index}
        style={{ '--w': `${resumePreviewLineWidths[index % resumePreviewLineWidths.length]}%` }}
      />
    ));

  const companyCategories = useMemo(() => ([
    { label: "一线互联网", icon: NetworkNode, iconColor: "#2563EB" },
    { label: "头部科技公司", icon: TechChip, iconColor: "#7C3AED" },
    { label: "大型银行 / 金融", icon: FinanceBuilding, iconColor: "#0891B2" },
    { label: "独角兽 / 高成长企业", icon: GrowthCurve, iconColor: "#DB2777" },
    { label: "外企 / 跨国公司", icon: GlobalSphere, iconColor: "#059669" },
    { label: "知名国企 / 央企", icon: StateBadge, iconColor: "#D97706" },
    { label: "创业公司 / 初创企业", icon: StartupSeed, iconColor: "#EA580C" },
    { label: "咨询公司 / 专业服务", icon: ServiceFlow, iconColor: "#4F46E5" },
    { label: "电商 / 零售", icon: RetailTag, iconColor: "#DC2626" }
  ]), []);

  const companyCarouselBackgroundColor = '#6366F1';

  // 默认概念图模板（作为fallback）
  const defaultResumeTemplates = useMemo(() => ([
    {
      id: 'popular',
      name: '热门推荐',
      accent: 'linear-gradient(135deg, #60A5FA 0%, #6366F1 60%, #8B5CF6 100%)',
      chips: ['社招', '通用', '投递友好'],
      previewImage: null,
      isConceptual: true
    },
    {
      id: 'minimal',
      name: '极简白',
      accent: 'linear-gradient(135deg, #94A3B8 0%, #64748B 55%, #475569 100%)',
      chips: ['校招', '简洁', '一页重点'],
      previewImage: null,
      isConceptual: true
    },
    {
      id: 'sidebar',
      name: '深蓝侧栏',
      accent: 'linear-gradient(135deg, #0EA5E9 0%, #2563EB 55%, #1D4ED8 100%)',
      chips: ['技术岗', '结构清晰', '信息密度'],
      previewImage: null,
      isConceptual: true
    }
  ]), []);

  // 本地fallback路径（如果API失败时使用）
  const localFallbackTemplates = useMemo(() => [
    {
      id: '001',
      name: '蓝色通用',
      accent: 'linear-gradient(135deg, #60A5FA 0%, #6366F1 60%, #8B5CF6 100%)',
      chips: ['社招', '通用', '投递友好'],
      previewImage: '/images/resume-templates/001/蓝色通用.png',
      isConceptual: false
    },
    {
      id: '002',
      name: '绿色清爽',
      accent: 'linear-gradient(135deg, #10B981 0%, #059669 55%, #047857 100%)',
      chips: ['校招', '简洁', '一页重点'],
      previewImage: '/images/resume-templates/002/绿色清爽.png',
      isConceptual: false
    },
    {
      id: '003',
      name: '运营求职',
      accent: 'linear-gradient(135deg, #F59E0B 0%, #D97706 55%, #B45309 100%)',
      chips: ['运营', '结构清晰', '信息密度'],
      previewImage: '/images/resume-templates/003/运营求职.png',
      isConceptual: false
    }
  ], []);

  // 获取真实简历模板
  useEffect(() => {
    const fetchRealTemplates = async () => {
      try {
        setTemplatesLoading(true);

        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        if (!token) {
          const normalizedFallbacks = localFallbackTemplates.map(t => ({
            ...t,
            previewImage: t.previewImage ? normalizePreviewPath(t.previewImage) : null
          }));
          setRealResumeTemplates(normalizedFallbacks);
          setTemplatesLoading(false);
          return;
        }

        const response = await resumeTemplatesApi.list({
          page: 1,
          page_size: 3,
          sort: 'download_count',
          order: 'desc'
        });

        if (response?.code === 0 && response?.data?.items?.length > 0) {
          const templates = response.data.items.slice(0, 3).map((item, index) => {
            // 解析tags字符串（可能是逗号分隔或JSON）
            let chips = [];
            if (item.tags) {
              try {
                const parsed = JSON.parse(item.tags);
                chips = Array.isArray(parsed) ? parsed : [item.tags];
              } catch {
                chips = item.tags.split(',').map(t => t.trim()).filter(t => t);
              }
            }

            // 如果没有tags，使用默认的
            if (chips.length === 0) {
              chips = defaultResumeTemplates[index]?.chips || ['通用', '专业', '实用'];
            }

            // 根据模板名称或类型选择合适的渐变色
            const getAccentColor = (name, category) => {
              if (name?.includes('蓝色') || name?.includes('蓝')) {
                return 'linear-gradient(135deg, #60A5FA 0%, #6366F1 60%, #8B5CF6 100%)';
              }
              if (name?.includes('绿色') || name?.includes('绿')) {
                return 'linear-gradient(135deg, #10B981 0%, #059669 55%, #047857 100%)';
              }
              if (name?.includes('黑色') || name?.includes('黑')) {
                return 'linear-gradient(135deg, #475569 0%, #334155 55%, #1e293b 100%)';
              }
              if (name?.includes('极简') || name?.includes('简洁')) {
                return 'linear-gradient(135deg, #94A3B8 0%, #64748B 55%, #475569 100%)';
              }
              // 默认蓝色渐变
              return 'linear-gradient(135deg, #0EA5E9 0%, #2563EB 55%, #1D4ED8 100%)';
            };

            const rawPreviewPath = item.preview_image_path;
            let previewImage = rawPreviewPath ? normalizePreviewPath(rawPreviewPath) : '';

            if (!previewImage) {
              const fallbackTemplate = localFallbackTemplates.find((t) => {
                if (!t.name || !item.name) return false;
                const fallbackName = String(t.name);
                const itemName = String(item.name);
                return (
                  fallbackName === itemName ||
                  fallbackName.includes(itemName) ||
                  itemName.includes(fallbackName)
                );
              });

              if (fallbackTemplate?.previewImage) {
                previewImage = normalizePreviewPath(fallbackTemplate.previewImage);
              }
            }

            return {
              id: item.id.toString(),
              name: item.name || `模板${index + 1}`,
              accent: getAccentColor(item.name, item.category),
              chips: chips.slice(0, 3), // 最多显示3个标签
              previewImage: previewImage || null,
              isConceptual: false,
              templateId: item.id
            };
          });

          // 确保有3个模板，不足时用默认模板补充
          while (templates.length < 3) {
            const defaultIndex = templates.length;
            const fallbackTemplate = localFallbackTemplates[defaultIndex];
            templates.push({
              ...defaultResumeTemplates[defaultIndex],
              previewImage: fallbackTemplate?.previewImage ? normalizePreviewPath(fallbackTemplate.previewImage) : null
            });
          }

          setRealResumeTemplates(templates);
        } else {
          // API返回空数据，使用本地fallback
          const normalizedFallbacks = localFallbackTemplates.map(t => ({
            ...t,
            previewImage: t.previewImage ? normalizePreviewPath(t.previewImage) : null
          }));
          setRealResumeTemplates(normalizedFallbacks);
        }
      } catch (error) {
        console.warn('获取简历模板失败，使用本地fallback:', error);
        // API失败时使用本地fallback
        const normalizedFallbacks = localFallbackTemplates.map(t => ({
          ...t,
          previewImage: t.previewImage ? normalizePreviewPath(t.previewImage) : null
        }));
        setRealResumeTemplates(normalizedFallbacks);
      } finally {
        setTemplatesLoading(false);
      }
    };

    fetchRealTemplates();
  }, [normalizePreviewPath, localFallbackTemplates]);

  // 使用真实模板或fallback
  const resumeTemplates = useMemo(() => {
    if (realResumeTemplates.length > 0) {
      return realResumeTemplates;
    }
    return defaultResumeTemplates;
  }, [realResumeTemplates, defaultResumeTemplates]);

  useEffect(() => {
    if (isResumePreviewHovered) {
      return;
    }
    const intervalId = setInterval(() => {
      setActiveResumeTemplateIndex((prev) => (prev + 1) % resumeTemplates.length);
    }, 4200);
    return () => clearInterval(intervalId);
  }, [isResumePreviewHovered, resumeTemplates.length]);

  useEffect(() => {
    const element = companiesFirstGroupRef.current;
    if (!element) return;

    const measure = () => {
      const width = Math.ceil(element.getBoundingClientRect().width);
      setCompaniesScrollDistance(width);
    };

    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(element);
      return () => resizeObserver.disconnect();
    }

    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const homeStructuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        url: 'https://zhimai.ai',
        name: '职唛AI',
        description: 'AI驱动的智能求职平台',
        inLanguage: 'zh-CN'
      },
      {
        '@type': 'Organization',
        name: '职唛AI',
        legalName: '杭州元烁智创科技有限公司',
        url: 'https://zhimai.ai',
        logo: 'https://zhimai.ai/images/logos/logo.svg',
        description: '职唛AI提供AI驱动的面试准备服务',
        address: {
          '@type': 'PostalAddress',
          addressLocality: '杭州',
          addressRegion: '浙江省',
          addressCountry: 'CN'
        },
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'contact@zhispark.com',
          contactType: '客户服务',
          availableLanguage: ['Chinese', 'English', 'Japanese', 'Korean']
        }
      },
      {
        '@type': 'Service',
        serviceType: '求职面试辅导',
        provider: {
          '@type': 'Organization',
          name: '职唛AI'
        },
        areaServed: 'CN',
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.8',
          reviewCount: '10000'
        }
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '支持哪些类型的面试准备?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '职唛AI提供全流程面试准备服务,包括:简历诊断、面试押题、模拟面试、会议助手等。无论是技术面试、行为面试还是系统设计,都能为你提供专业指导。'
            }
          },
          {
            '@type': 'Question',
            name: '模拟面试是如何进行的?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '模拟面试采用实时语音对话方式,AI面试官会根据你选择的岗位和简历内容,提出真实的面试问题。你可以通过语音或文字回答,AI会实时分析你的表现。'
            }
          },
          {
            '@type': 'Question',
            name: '简历诊断的准确性如何?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '职唛AI的简历诊断基于大语言模型和行业知识库,能够从多个维度分析简历:可搜索性、技能匹配度、招聘官视角、行业匹配度等。'
            }
          }
        ]
      }
    ]
  };



  return (
    <HomeContainer>
      <SEO
        title="职唛AI - AI驱动的智能求职平台 | 模拟面试 | 简历优化 | 面试辅导"
        description="职唛AI提供全流程AI面试准备服务:智能简历诊断、精准面试押题、1对1模拟面试训练、会议助手。已帮助10,000+求职者成功拿到阿里、腾讯、字节等名企offer,面试通过率提升95%。支持中英日韩多语言面试。"
        keywords="AI面试,模拟面试,简历优化,面试准备,求职辅导,面试辅导,职业发展,面试技巧,简历诊断,面试押题,会议助手,阿里面试,腾讯面试,字节跳动面试,百度面试,美团面试"
        canonicalUrl="https://zhimai.ai"
        ogImage="https://zhimai.ai/images/og-image.png"
        structuredData={homeStructuredData}
      />
      <HeroSection>
        <HeroContent>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <MainTitle>
              AI驱动的<br />
              <HighlightText>智能求职平台</HighlightText>
            </MainTitle>
            <Subtitle>
              全流程AI指导，个性化简历分析，模拟面试训练。<br />
              开启你的免费体验，助力梦想职业！🚀
            </Subtitle>
            <CTAGroup>
              <CTAButton onClick={handleGetStarted}>
                立即登录体验 <ArrowRight size={20} />
              </CTAButton>
              <HeroDocumentLink
                href="https://my.feishu.cn/wiki/O0m4wLGihiedrNkBSLdcvhQ7ntO"
                target="_blank"
                rel="noopener noreferrer"
              >
                产品使用文档 <ExternalLink size={18} />
              </HeroDocumentLink>
            </CTAGroup>
          </motion.div>
        </HeroContent>

        <HeroVideo>
          <PromoText>{isMobile ? '' : '面试不打怵，拿Offer快人一步'}</PromoText>
          <VideoContainer>
            <DemoVideo>
              <AnimatedHero />
            </DemoVideo>
          </VideoContainer>
        </HeroVideo>
      </HeroSection>

      <CompaniesSection ref={companiesSectionRef}>
        <CompaniesTitle>为理想的公司做好面试准备</CompaniesTitle>
        <CompaniesContainer>
          <CompaniesTrack
            $isAnimating={isCompaniesVisible}
            style={{
              '--scroll-distance': `${companiesScrollDistance}px`,
              '--scroll-duration': `${companiesScrollDistance ? Math.max(35, companiesScrollDistance / 40) : 55}s`
            }}
          >
            <CompaniesGroup ref={companiesFirstGroupRef}>
              {companyCategories.map((category, index) => (
                <CategoryBadge
                  key={`first-${index}`}
                >
                  <CategoryIcon $color={companyCarouselBackgroundColor}>
                    <category.icon color={category.iconColor} size={26} animated={false} glow={false} />
                  </CategoryIcon>
                  <CategoryLabel>{category.label}</CategoryLabel>
                </CategoryBadge>
              ))}
            </CompaniesGroup>
            <CompaniesGroup aria-hidden="true">
              {companyCategories.map((category, index) => (
                <CategoryBadge
                  key={`second-${index}`}
                >
                  <CategoryIcon $color={companyCarouselBackgroundColor}>
                    <category.icon color={category.iconColor} size={26} animated={false} glow={false} />
                  </CategoryIcon>
                  <CategoryLabel>{category.label}</CategoryLabel>
                </CategoryBadge>
              ))}
            </CompaniesGroup>
            <CompaniesGroup aria-hidden="true">
              {companyCategories.map((category, index) => (
                <CategoryBadge
                  key={`third-${index}`}
                >
                  <CategoryIcon $color={companyCarouselBackgroundColor}>
                    <category.icon color={category.iconColor} size={26} animated={false} glow={false} />
                  </CategoryIcon>
                  <CategoryLabel>{category.label}</CategoryLabel>
                </CategoryBadge>
              ))}
            </CompaniesGroup>
          </CompaniesTrack>
        </CompaniesContainer>
      </CompaniesSection>

      <FeaturesSection>
        <SectionTitle>还在为求职失败而烦恼吗？</SectionTitle>
        <FeaturesGrid>
          <FeatureCard>
            <FeatureIcon $color="#3B82F6">
              <FileText size={32} color="#3B82F6" />
            </FeatureIcon>
            <FeatureTitle>简历投了100份，却石沉大海？</FeatureTitle>
            <FeatureDescription>
              简历千篇一律，HR看都不看就pass？AI深度分析你的简历，精准定位问题，让HR眼前一亮，投递成功率提升300%
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon $color="#8B5CF6">
              <Target size={32} color="#8B5CF6" />
            </FeatureIcon>
            <FeatureTitle>面试聊得很好，第二天却说"不合适"？</FeatureTitle>
            <FeatureDescription>
              不知道面试官真正想要什么，答非所问？基于简历内和岗位信息，AI精准预测面试题目，让你提前掌握面试重点，通过率提升95%
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon $color="#06B6D4">
              <Video size={32} color="#06B6D4" />
            </FeatureIcon>
            <FeatureTitle>面试时紧张到说不出话？</FeatureTitle>
            <FeatureDescription>
              缺乏实战经验，一到面试就大脑空白？1对1真实模拟面试，AI实时反馈表现，快速提升面试技巧和自信心，告别紧张
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon $color="#10B981">
              <Bot size={32} color="#10B981" />
            </FeatureIcon>
            <FeatureTitle>求职路上孤军奋战，没人指导？</FeatureTitle>
            <FeatureDescription>
              不知道哪里出了问题，只能盲目投简历？AI全程陪伴，从简历优化到面试成功，提供个性化指导，让你的求职之路不再孤单
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon $color="#F59E0B">
              <Zap size={32} color="#F59E0B" />
            </FeatureIcon>
            <FeatureTitle>时间不够，没空准备面试？</FeatureTitle>
            <FeatureDescription>
              工作忙，没时间系统准备面试？随时随地练习，无需预约，想练就练，碎片化时间也能高效提升面试能力
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon $color="#EF4444">
              <TrendingUp size={32} color="#EF4444" />
            </FeatureIcon>
            <FeatureTitle>不知道自己的问题在哪里？</FeatureTitle>
            <FeatureDescription>
              面试失败却不知道为什么，重复同样的错误？详细的能力分析报告和成长轨迹，让你清楚看到每一次进步，精准定位问题
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>

        <FeatureHighlight>
          <HighlightContent>
            <HighlightTitle>别再让机会从指缝间溜走！</HighlightTitle>
            <HighlightSubtitle>
              每浪费一天，就错过一个心仪的工作机会。现在开始，让AI帮你逆袭求职路！
            </HighlightSubtitle>
            <HighlightStats>
              <AnimatedStatItem number={10000} label="成功逆袭者" suffix="+" />
              <AnimatedStatItem number={95} label="面试通过率" suffix="%" />
              <AnimatedStatItem number={500} label="服务企业" suffix="+" />
            </HighlightStats>
          </HighlightContent>
        </FeatureHighlight>
      </FeaturesSection>

      <TestimonialsSection>
        <TestimonialsHeader>
          <TestimonialsTitle>优秀人才的共同选择</TestimonialsTitle>
          <TestimonialsSubtitle>
            加入我们的求职者社区，他们通过职唛AI提升面试能力，
            最终成功入职理想企业，实现职业目标
          </TestimonialsSubtitle>
        </TestimonialsHeader>
        <TestimonialsSectionContent />
      </TestimonialsSection>

      <HowItWorksSection>
        <SectionTitle>如何开始你的求职之旅</SectionTitle>
        <StepsContainer>
          <StepItem>
            <StepNumber>01</StepNumber>
            <StepTitle>上传简历</StepTitle>
            <StepDescription>上传你的简历，AI立即开始分析优化</StepDescription>
          </StepItem>
          <StepConnector>→</StepConnector>
          <StepItem>
            <StepNumber>02</StepNumber>
            <StepTitle>AI诊断</StepTitle>
            <StepDescription>获得专业的简历优化建议和面试准备</StepDescription>
          </StepItem>
          <StepConnector>→</StepConnector>
          <StepItem>
            <StepNumber>03</StepNumber>
            <StepTitle>模拟训练</StepTitle>
            <StepDescription>针对不同岗位一对一面试练习，不断提升面试表现</StepDescription>
          </StepItem>
          <StepConnector>→</StepConnector>
          <StepItem>
            <StepNumber>04</StepNumber>
            <StepTitle>成功入职</StepTitle>
            <StepDescription>充满信心地参加面试，获得心仪工作</StepDescription>
          </StepItem>
        </StepsContainer>
      </HowItWorksSection>

      {/* 简历模板免费下载宣传模块 */}
      <ResumeTemplateSection>
        <ResumeTemplateContainer>
          <ResumeTemplateHeader>
            <ResumeTemplateHeaderLeft>
              <ResumeTemplateTitle>
                热门简历模板<span>免费下载</span>
              </ResumeTemplateTitle>
              <ResumeTemplateSubtitle>
                精选优质简历模板，登录后即可下载
              </ResumeTemplateSubtitle>

              <ResumeTemplateStatsRow>
                <ResumeTemplateStat>
                  <ResumeTemplateStatValue>精选</ResumeTemplateStatValue>
                  <ResumeTemplateStatLabel>优质模板</ResumeTemplateStatLabel>
                </ResumeTemplateStat>
                <ResumeTemplateStatDivider />
                <ResumeTemplateStat>
                  <ResumeTemplateStatValue>即用</ResumeTemplateStatValue>
                  <ResumeTemplateStatLabel>直接下载</ResumeTemplateStatLabel>
                </ResumeTemplateStat>
              </ResumeTemplateStatsRow>

              <ResumeTemplateActions>
                <ResumeTemplatePrimaryCTA onClick={handleGetStarted}>
                  立即下载
                  <ArrowRight size={18} />
                </ResumeTemplatePrimaryCTA>
              </ResumeTemplateActions>
            </ResumeTemplateHeaderLeft>

            <ResumeTemplatePreview
              aria-label="简历模板预览"
              onMouseEnter={() => setIsResumePreviewHovered(true)}
              onMouseLeave={() => setIsResumePreviewHovered(false)}
            >
              <ResumeTemplatePreviewStack>
                {resumeTemplates.map((tpl, index) => (
                  <ResumeTemplatePreviewCard
                    key={tpl.id}
                    $index={index}
                    $active={index === activeResumeTemplateIndex}
                    $accent={tpl.accent}
                    onMouseEnter={() => setActiveResumeTemplateIndex(index)}
                    onFocus={() => setActiveResumeTemplateIndex(index)}
                  >
                    {tpl.previewImage && !tpl.isConceptual ? (
                      // 显示真实预览图
                      <ResumePreviewImageContainer>
                        <ResumePreviewPicture>
                          {/* WebP格式：更好的压缩率和质量 */}
                          {(() => {
                            // 检查是否是PNG图片，如果是则生成WebP路径
                            if (tpl.previewImage && /\.png$/i.test(tpl.previewImage)) {
                              const webpSrc = tpl.previewImage.replace(/\.png$/i, '.webp');
                              // 对于本地路径（/images/开头）或相对路径，提供WebP支持
                              // 对于OSS URL，也尝试WebP（如果OSS上有对应文件）
                              if (webpSrc !== tpl.previewImage) {
                                return <source srcSet={webpSrc} type="image/webp" />;
                              }
                            }
                            return null;
                          })()}
                          {/* PNG格式：作为fallback，兼容不支持WebP的浏览器 */}
                          <ResumePreviewImage
                            src={tpl.previewImage}
                            alt={tpl.name}
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              // 图片加载失败时，回退到概念图
                              console.warn(`预览图加载失败: ${tpl.previewImage}`);
                              e.target.style.display = 'none';
                            }}
                          />
                        </ResumePreviewPicture>
                        <ResumePreviewOverlay>
                          <ResumePreviewTopBar $accent={tpl.accent}>
                            <ResumePreviewTopTitle>{tpl.name}</ResumePreviewTopTitle>
                          </ResumePreviewTopBar>
                          <ResumePreviewChips>
                            {tpl.chips.map((chip) => (
                              <ResumePreviewChip key={chip}>{chip}</ResumePreviewChip>
                            ))}
                          </ResumePreviewChips>
                        </ResumePreviewOverlay>
                      </ResumePreviewImageContainer>
                    ) : (
                      // 显示概念图（fallback）
                      <>
                        <ResumePreviewTopBar $accent={tpl.accent}>
                          <ResumePreviewTopTitle>{tpl.name}</ResumePreviewTopTitle>
                          <ResumePreviewAvatar />
                        </ResumePreviewTopBar>
                        <ResumePreviewMetaRow>
                          <ResumePreviewMetaPill />
                          <ResumePreviewMetaPill />
                          <ResumePreviewMetaPill />
                        </ResumePreviewMetaRow>
                        <ResumePreviewSection>
                          <ResumePreviewSectionTitle />
                          <ResumePreviewLines>
                            {renderResumePreviewLines(4)}
                          </ResumePreviewLines>
                        </ResumePreviewSection>
                        <ResumePreviewSection>
                          <ResumePreviewSectionTitle />
                          <ResumePreviewLines>
                            {renderResumePreviewLines(5)}
                          </ResumePreviewLines>
                        </ResumePreviewSection>
                        <ResumePreviewSection>
                          <ResumePreviewSectionTitle />
                          <ResumePreviewLines>
                            {renderResumePreviewLines(3)}
                          </ResumePreviewLines>
                        </ResumePreviewSection>
                        <ResumePreviewChips>
                          {tpl.chips.map((chip) => (
                            <ResumePreviewChip key={chip}>{chip}</ResumePreviewChip>
                          ))}
                        </ResumePreviewChips>
                      </>
                    )}
                  </ResumeTemplatePreviewCard>
                ))}
              </ResumeTemplatePreviewStack>
            </ResumeTemplatePreview>
          </ResumeTemplateHeader>
        </ResumeTemplateContainer>
      </ResumeTemplateSection>

      <FAQSectionComponent />

      <Footer showDisclaimer={true} />
    </HomeContainer>
  );
};

// Styled Components
const HomeContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  color: #1a1a1a;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 4rem;
  padding: 8rem 2rem 4rem;
  max-width: 1300px;
  margin: 0 auto;
  align-items: center;
  min-height: 80vh;

  @media (min-width: 1920px) {
    max-width: 1600px;
    gap: 6rem;
    padding: 10rem 3rem 6rem;
    min-height: 85vh;
  }

  @media (min-width: 2560px) {
    max-width: 1800px;
    gap: 8rem;
    padding: 12rem 4rem 8rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 6rem 1rem 2rem;
  }
`;

const HeroContent = styled.div`
  z-index: 2;
`;

const MainTitle = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  font-weight: 900;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: #1a1a1a;

  @media (min-width: 1920px) {
    font-size: clamp(4rem, 4.5vw, 5.5rem);
    margin-bottom: 2rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(5rem, 5vw, 6.5rem);
    margin-bottom: 2.5rem;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HighlightText = styled.span`
  background: linear-gradient(45deg, #6366F1, #8B5CF6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 900;
`;

const Subtitle = styled.p`
  font-size: clamp(1rem, 1.5vw, 1.4rem);
  line-height: 1.6;
  margin-bottom: 2rem;
  color: #64748b;

  @media (min-width: 1920px) {
    font-size: clamp(1.3rem, 1.8vw, 1.6rem);
    margin-bottom: 2.5rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(1.5rem, 2vw, 1.8rem);
    margin-bottom: 3rem;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }
`;

const CTAGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const CTAButton = styled.button`
  background: linear-gradient(45deg, #6366F1, #8B5CF6);
  border: none;
  padding: clamp(0.875rem, 1.2vw, 1.25rem) clamp(1.5rem, 2.5vw, 2.5rem);
  border-radius: 16px;
  color: white;
  font-size: clamp(1rem, 1.2vw, 1.3rem);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: transform 0.2s, box-shadow 0.2s;
  min-height: 44px; /* 确保触摸目标足够大 */

  @media (min-width: 1920px) {
    padding: 1.25rem 2.5rem;
    font-size: 1.3rem;
  }

  @media (min-width: 2560px) {
    padding: 1.5rem 3rem;
    font-size: 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
    min-height: 44px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(99, 102, 241, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const HeroVideo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const PromoText = styled.div`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  padding: 0;
  font-size: clamp(18px, 1.5vw, 28px);
  font-weight: 700;
  background: linear-gradient(45deg, #6366F1, #8B5CF6, #A855F7, #6366F1);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: absolute;
  top: -70px;
  left: 35%;
  transform: translateX(-50%);
  z-index: 10;
  text-shadow: 0 0 8px rgba(99, 102, 241, 0.3);
  transition: all 0.3s ease;
  animation: pulse 2s ease-in-out infinite, metallicShine 3s ease-in-out infinite;

  @media (min-width: 1920px) {
    font-size: clamp(24px, 1.8vw, 32px);
    top: -80px;
  }

  @media (min-width: 2560px) {
    font-size: clamp(28px, 2vw, 36px);
    top: -90px;
  }

  @keyframes pulse {
    0%, 100% { 
      transform: translateX(-50%) scale(1);
      text-shadow: 0 0 8px rgba(99, 102, 241, 0.3);
    }
    50% { 
      transform: translateX(-50%) scale(1.05);
      text-shadow: 0 0 15px rgba(99, 102, 241, 0.6);
    }
  }

  @keyframes metallicShine {
    0% {
      background-position: 0% 50%;
      filter: brightness(1) contrast(1);
    }
    25% {
      background-position: 100% 50%;
      filter: brightness(1.2) contrast(1.1);
    }
    50% {
      background-position: 200% 50%;
      filter: brightness(1.4) contrast(1.2);
    }
    75% {
      background-position: 100% 50%;
      filter: brightness(1.2) contrast(1.1);
    }
    100% {
      background-position: 0% 50%;
      filter: brightness(1) contrast(1);
    }
  }

  &:hover {
    /* 悬浮时保持原样，不做任何变化 */
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    animation: none;
    background: #6366F1;
    -webkit-background-clip: unset;
    -webkit-text-fill-color: #6366F1;
    background-clip: unset;
    filter: none;
  }
  
  @media (max-width: 768px) {
    font-size: 18px;
    top: -60px;
    left: 30%;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 580px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);

  @media (min-width: 1920px) {
    max-width: 680px;
  }

  @media (min-width: 2560px) {
    max-width: 780px;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    border-radius: 16px;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const DemoVideo = styled.div`
  aspect-ratio: 16/9;
  position: relative;
`;

const FeaturesSection = styled.section`
  padding: 1.5rem 2rem 4rem;
  margin-top: 1.5rem;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: 1920px) {
    max-width: 1500px;
    padding: 2rem 3rem 5rem;
    margin-top: 2rem;
  }

  @media (min-width: 2560px) {
    max-width: 1700px;
    padding: 2.5rem 4rem 6rem;
    margin-top: 2.5rem;
  }

  @media (max-width: 768px) {
    padding: 1rem 1rem 3rem;
    margin-top: 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 3.5vw, 3rem);
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
  color: #1a1a1a;

  @media (min-width: 1920px) {
    font-size: clamp(3rem, 3.5vw, 3.5rem);
    margin-bottom: 4rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(3.5rem, 4vw, 4rem);
    margin-bottom: 5rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 3rem;

  @media (min-width: 1920px) {
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 2.5rem;
    margin-top: 4rem;
  }

  @media (min-width: 2560px) {
    grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
    gap: 3rem;
    margin-top: 5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-top: 2rem;
  }
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: ${props => props.$color ? `linear-gradient(135deg, ${props.$color}10 0%, ${props.$color}05 100%)` : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'};
  border: 1px solid ${props => props.$color ? `${props.$color}30` : '#e2e8f0'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  box-shadow: 0 4px 16px ${props => props.$color ? `${props.$color}20` : 'rgba(0, 0, 0, 0.05)'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 30%, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 60%);
    border-radius: 50%;
    opacity: 0.6;
  }

  svg {
    filter: drop-shadow(0 4px 6px ${props => props.$color ? `${props.$color}40` : 'rgba(0, 0, 0, 0.1)'});
    transition: all 0.3s ease;
    z-index: 1;
  }

  @media (min-width: 1920px) {
    width: 100px;
    height: 100px;
    margin: 0 auto 1.25rem;
  }

  @media (min-width: 2560px) {
    width: 120px;
    height: 120px;
    margin: 0 auto 1.5rem;
  }
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 24px ${props => props.$color ? `${props.$color}30` : 'rgba(0, 0, 0, 0.1)'};
    background: ${props => props.$color ? `linear-gradient(135deg, ${props.$color}15 0%, ${props.$color}08 100%)` : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'};
    
    svg {
      filter: drop-shadow(0 6px 8px ${props => props.$color ? `${props.$color}50` : 'rgba(0, 0, 0, 0.15)'});
      transform: scale(1.1);
    }
  }
`;

const FeatureCard = styled.div`
  background: transparent;
  border: none;
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  @media (min-width: 1920px) {
    padding: 2.5rem;
    border-radius: 24px;
  }

  @media (min-width: 2560px) {
    padding: 3rem;
    border-radius: 28px;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 16px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);

    &::before {
      left: 100%;
    }

    ${FeatureIcon} {
      transform: scale(1.1) rotate(3deg);
    }
  }
`;

const FeatureTitle = styled.h3`
  font-size: clamp(1.25rem, 1.8vw, 1.75rem);
  font-weight: 600;
  margin-bottom: 1rem;
  color: #1a1a1a;
  transition: color 0.3s ease;

  @media (min-width: 1920px) {
    font-size: clamp(1.5rem, 2vw, 2rem);
    margin-bottom: 1.25rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(1.75rem, 2.2vw, 2.25rem);
    margin-bottom: 1.5rem;
  }
  
  ${FeatureCard}:hover & {
    color: #6366F1;
  }
`;

const FeatureDescription = styled.p`
  color: #64748b;
  line-height: 1.6;
  font-size: clamp(0.9rem, 1.2vw, 1.1rem);
  transition: color 0.3s ease;

  @media (min-width: 1920px) {
    font-size: clamp(1rem, 1.3vw, 1.2rem);
  }

  @media (min-width: 2560px) {
    font-size: clamp(1.1rem, 1.4vw, 1.3rem);
  }
  
  ${FeatureCard}:hover & {
    color: #475569;
  }
`;

// 简历模板宣传模块样式
const ResumeTemplateSection = styled.div`
  margin: 1.5rem 0 2.5rem;
  padding: 3.5rem 0;
  position: relative;
  background: linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 100%);
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(900px 360px at 10% 30%, rgba(99, 102, 241, 0.08), transparent 60%),
      radial-gradient(800px 320px at 92% 40%, rgba(139, 92, 246, 0.05), transparent 62%);
    pointer-events: none;
  }

  @media (min-width: 1920px) {
    margin: 2.5rem 0 3rem;
    padding: 4.5rem 0;
  }

  @media (min-width: 2560px) {
    margin: 3rem 0 3.5rem;
    padding: 5.5rem 0;
  }

  @media (max-width: 768px) {
    margin: 1.5rem 0 2rem;
    padding: 2.5rem 0;
  }
`;

const ResumeTemplateContainer = styled.div`
  position: relative;
  z-index: 1;
  background: transparent;
  border: none;
  box-shadow: none;
  border-radius: 0;
  padding: 0;
  position: relative;
  overflow: hidden;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem 0 3rem;

  @media (min-width: 1920px) {
    max-width: 1500px;
    padding: 0 2.5rem 0 4rem;
  }

  @media (min-width: 2560px) {
    max-width: 1700px;
    padding: 0 3rem 0 5rem;
  }

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const ResumeTemplateHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 4rem;
  margin-bottom: 3rem;

  @media (min-width: 1920px) {
    gap: 5rem;
    margin-bottom: 4rem;
  }

  @media (min-width: 2560px) {
    gap: 6rem;
    margin-bottom: 5rem;
  }

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 3rem;
    align-items: center;
  }
`;

const ResumeTemplateHeaderLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  max-width: 480px;
  gap: 0;
  padding-top: 3rem;

  @media (min-width: 1920px) {
    padding-top: 4rem;
  }

  @media (min-width: 2560px) {
    padding-top: 5rem;
  }

  @media (max-width: 1024px) {
    align-items: center;
    text-align: center;
    max-width: 100%;
    padding-top: 2rem;
  }

  @media (max-width: 768px) {
    padding-top: 1.5rem;
  }
`;

const ResumeTemplateTitle = styled.h2`
  font-size: clamp(2rem, 3.2vw, 2.75rem);
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.02em;
  line-height: 1.2;
  margin: 0;
  margin-bottom: 1rem;
  text-align: left;

  span {
    background: linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #A855F7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (min-width: 1920px) {
    font-size: clamp(2.75rem, 3.1vw, 3.25rem);
    margin-bottom: 1.25rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(3.25rem, 3.5vw, 3.75rem);
    margin-bottom: 1.5rem;
  }

  @media (max-width: 1024px) {
    text-align: center;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 0.75rem;
  }
`;

const ResumeTemplateSubtitle = styled.p`
  font-size: clamp(1rem, 1.25vw, 1.15rem);
  color: #475569;
  line-height: 1.6;
  margin: 0;
  margin-bottom: 2rem;
  text-align: left;

  @media (min-width: 1920px) {
    font-size: clamp(1.15rem, 1.2vw, 1.35rem);
    margin-bottom: 2.5rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(1.3rem, 1.3vw, 1.5rem);
    margin-bottom: 3rem;
  }

  @media (max-width: 1024px) {
    text-align: center;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.25rem;
  }
`;

const ResumeTemplateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;
`;

const ResumeTemplateCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: center;
  text-align: center;
  width: 100%;
`;

const ResumeTemplateStatsRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 3rem;
  margin: 0;
  margin-bottom: 2.5rem;

  @media (min-width: 1920px) {
    margin-bottom: 3rem;
  }

  @media (min-width: 2560px) {
    margin-bottom: 3.5rem;
  }

  @media (max-width: 1024px) {
    justify-content: center;
    margin-bottom: 2rem;
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 1rem 2rem;
    margin-bottom: 1.5rem;
  }
`;

const ResumeTemplateStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const ResumeTemplateStatValue = styled.div`
  font-size: 1.25rem;
  font-weight: 800;
  color: #0f172a;

  @media (min-width: 1920px) {
    font-size: 1.35rem;
  }
`;

const ResumeTemplateStatLabel = styled.div`
  font-size: 0.95rem;
  color: #64748b;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ResumeTemplateStatDivider = styled.div`
  width: 1px;
  height: 34px;
  background: linear-gradient(to bottom, transparent, rgba(148, 163, 184, 0.55), transparent);
`;

const ResumeTemplateBullets = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-top: 0.2rem;
`;

const ResumeTemplateBullet = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: #334155;
  line-height: 1.55;
  font-size: 1rem;
`;

const ResumeTemplateBulletIcon = styled.div`
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 7px;
  background: rgba(99, 102, 241, 0.12);
  border: 1px solid rgba(99, 102, 241, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6366F1;
`;

const ResumeTemplateActions = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: flex-start;
  align-items: center;
  margin: 0;

  @media (max-width: 1024px) {
    justify-content: center;
  }

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
`;

const ResumeTemplatePrimaryCTA = styled.button`
  background: linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%);
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  color: white;
  font-size: clamp(1rem, 1.2vw, 1.2rem);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(99, 102, 241, 0.4);
    background: linear-gradient(135deg, #7C3AED 0%, #A855F7 100%);
  }

  &:active {
    transform: translateY(0);
  }

  @media (min-width: 1920px) {
    padding: 1.25rem 2.5rem;
    font-size: 1.2rem;
  }

  @media (min-width: 2560px) {
    padding: 1.5rem 3rem;
    font-size: 1.3rem;
  }

  @media (max-width: 768px) {
    justify-content: center;
    padding: 0.875rem 1.75rem;
    font-size: 1rem;
  }
`;

const ResumeTemplateNote = styled.div`
  font-size: 0.95rem;
  color: #64748b;
  margin-top: 0.15rem;
`;

const ResumeTemplatePreview = styled.div`
  position: relative;
  min-height: 550px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
  max-width: 500px;
  margin-top: 0;
  overflow: visible;
  padding: 2rem 0;

  @media (max-width: 1024px) {
    width: 100%;
    max-width: 100%;
    padding: 1.5rem 0;
    min-height: 500px;
  }

  @media (max-width: 980px) {
    min-height: 450px;
    padding: 1rem 0;
  }
`;

const ResumeTemplatePreviewStack = styled.div`
  position: relative;
  min-height: 500px;
  perspective: 1200px;
  overflow: visible;
  margin-bottom: 1rem;

  @media (max-width: 980px) {
    min-height: 450px;
  }
`;

const ResumeTemplatePreviewCard = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(calc(-50% + ${props => (props.$index - 1) * -10}px))
    translateY(${props => (props.$index - 1) * 14}px)
    rotateZ(${props => (props.$index - 1) * -3}deg)
    rotateY(${props => (props.$index - 1) * 6}deg);
  width: 100%;
  max-width: 430px;
  height: auto;
  min-height: 360px;
  max-height: none;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 18px;
  box-shadow: 0 30px 65px -45px rgba(15, 23, 42, 0.55);
  padding: 0.9rem 1rem 0.95rem;
  cursor: default;
  transition: transform 240ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 240ms cubic-bezier(0.4, 0, 0.2, 1), border-color 240ms cubic-bezier(0.4, 0, 0.2, 1);
  z-index: ${props => (props.$active ? 3 : props.$index)};
  overflow: hidden;

  ${props => props.$active && `
    transform: translateX(-50%) translateY(-10px) rotateZ(0deg) rotateY(0deg) scale(1.02);
    box-shadow: 0 55px 110px -70px rgba(15, 23, 42, 0.8);
    border-color: rgba(99, 102, 241, 0.45);
  `}

  &::after {
    content: '';
    position: absolute;
    left: 10px;
    right: 10px;
    top: 10px;
    height: 3px;
    border-radius: 999px;
    background: ${props => props.$accent || 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)'};
    opacity: ${props => (props.$active ? 0.9 : 0.35)};
    pointer-events: none;
  }

  &:hover {
    border-color: rgba(99, 102, 241, 0.45);
  }

  &:focus-visible {
    outline: 3px solid rgba(99, 102, 241, 0.35);
    outline-offset: 4px;
  }
`;

const ResumePreviewTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.7rem 0.85rem;
  border-radius: 14px;
  background: ${props => props.$accent || 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)'};
  color: white;
`;

const ResumePreviewTopTitle = styled.div`
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.01em;
`;

const ResumePreviewAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.92);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(15, 23, 42, 0.12), rgba(15, 23, 42, 0.04));
  }
`;

const ResumePreviewMetaRow = styled.div`
  display: flex;
  gap: 0.55rem;
  padding: 0.85rem 0.5rem 0.5rem;
`;

const ResumePreviewMetaPill = styled.div`
  height: 10px;
  flex: 1;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.35);
`;

const ResumePreviewSection = styled.div`
  padding: 0.6rem 0.5rem 0.2rem;
`;

const ResumePreviewSectionTitle = styled.div`
  height: 12px;
  width: 42%;
  border-radius: 999px;
  background: rgba(99, 102, 241, 0.2);
  margin-bottom: 0.55rem;
`;

const ResumePreviewLines = styled.div`
  display: grid;
  gap: 0.42rem;
`;

const ResumePreviewLine = styled.div`
  height: 10px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.24);
  width: var(--w, 88%);
`;

const ResumePreviewChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  padding: 0.75rem 0.5rem 0.1rem;
`;

const ResumePreviewChip = styled.div`
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  background: rgba(241, 245, 249, 0.95);
  border: 1px solid rgba(203, 213, 225, 0.75);
`;

const ResumePreviewImageContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 360px;
  max-height: 500px;
  overflow: hidden;
  border-radius: 14px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
`;

const ResumePreviewPicture = styled.picture`
  width: 100%;
  height: auto;
  display: block;
`;

const ResumePreviewImage = styled.img`
  width: 100%;
  height: auto;
  min-height: 360px;
  max-height: none;
  object-fit: contain;
  object-position: top;
  display: block;
  border-radius: 14px;
  background: #ffffff;
  
  /* 图片加载失败时的样式 */
  &[src=""],
  &:not([src]) {
    display: none;
  }
`;

const ResumePreviewOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.5) 50%, transparent 100%);
  padding: 1rem 0.85rem 0.75rem;
  border-radius: 0 0 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  pointer-events: none;
  
  ${ResumePreviewTopTitle} {
    color: white;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  }
  
  ${ResumePreviewChip} {
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: #1e293b;
    text-shadow: none;
  }
`;

const FeatureHighlight = styled.div`
  background: #f8fafc;
  padding: 2rem 2rem 1rem;
  border-radius: 20px;
  margin-top: 4rem;
  margin-bottom: 1rem;
  text-align: center;

  @media (min-width: 1920px) {
    padding: 3rem 3rem 1.5rem;
    border-radius: 24px;
    margin-top: 5rem;
  }

  @media (min-width: 2560px) {
    padding: 4rem 4rem 2rem;
    border-radius: 28px;
    margin-top: 6rem;
  }
`;

const HighlightContent = styled.div`
  max-width: 900px;
  margin: 0 auto;

  @media (min-width: 1920px) {
    max-width: 1100px;
  }

  @media (min-width: 2560px) {
    max-width: 1300px;
  }
`;

const HighlightTitle = styled.h3`
  font-size: clamp(2rem, 3.5vw, 3rem);
  font-weight: 700;
  margin-bottom: 1rem;
  color: #1a1a1a;

  @media (min-width: 1920px) {
    font-size: clamp(3rem, 3.5vw, 3.5rem);
    margin-bottom: 1.5rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(3.5rem, 4vw, 4rem);
    margin-bottom: 2rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const HighlightSubtitle = styled.p`
  font-size: clamp(1rem, 1.5vw, 1.4rem);
  line-height: 1.6;
  color: #64748b;
  margin-bottom: 2rem;

  @media (min-width: 1920px) {
    font-size: clamp(1.3rem, 1.8vw, 1.6rem);
    margin-bottom: 2.5rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(1.5rem, 2vw, 1.8rem);
    margin-bottom: 3rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const HighlightStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 4rem;
  margin-top: 1rem;

  @media (min-width: 1920px) {
    gap: 6rem;
    margin-top: 1.5rem;
  }

  @media (min-width: 2560px) {
    gap: 8rem;
    margin-top: 2rem;
  }
  
  @media (max-width: 768px) {
    gap: 2rem;
    flex-direction: column;
    align-items: center;
  }
`;

const StatItem = styled.div`
  text-align: center;
  min-width: 150px;
`;

const StatNumber = styled.h4`
  font-size: clamp(2rem, 3vw, 3rem);
  font-weight: 700;
  margin-bottom: 0.8rem;
  color: #1a1a1a;
  background: linear-gradient(45deg, #6366F1, #8B5CF6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (min-width: 1920px) {
    font-size: clamp(3rem, 3.5vw, 3.5rem);
    margin-bottom: 1rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(3.5rem, 4vw, 4rem);
    margin-bottom: 1.25rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const StatLabel = styled.p`
  color: #64748b;
  font-size: 1.1rem;
  font-weight: 500;
`;

const HowItWorksSection = styled.section`
  padding: 0.5rem 2rem 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (min-width: 1920px) {
    max-width: 1500px;
    padding: 1rem 3rem 3rem;
  }

  @media (min-width: 2560px) {
    max-width: 1700px;
    padding: 1.5rem 4rem 4rem;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem 2rem;
  }
`;

const StepsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 2rem;
  margin-bottom: 3rem;

  @media (min-width: 1920px) {
    gap: 3rem;
    margin-bottom: 4rem;
  }

  @media (min-width: 2560px) {
    gap: 4rem;
    margin-bottom: 5rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const StepItem = styled.div`
  text-align: center;
  flex: 1;

  @media (min-width: 1920px) {
    padding: 0 1rem;
  }

  @media (min-width: 2560px) {
    padding: 0 1.5rem;
  }
`;

const StepNumber = styled.div`
  display: inline-block;
  background: #6366F1;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin: 0 auto 1rem;
  font-size: 0.9rem;

  @media (min-width: 1920px) {
    width: 50px;
    height: 50px;
    font-size: 1.1rem;
    margin: 0 auto 1.25rem;
  }

  @media (min-width: 2560px) {
    width: 60px;
    height: 60px;
    font-size: 1.3rem;
    margin: 0 auto 1.5rem;
  }
`;

const StepTitle = styled.h3`
  font-size: clamp(1.1rem, 1.5vw, 1.5rem);
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1a1a1a;

  @media (min-width: 1920px) {
    font-size: clamp(1.5rem, 1.8vw, 1.75rem);
    margin-bottom: 0.75rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(1.75rem, 2vw, 2rem);
    margin-bottom: 1rem;
  }
`;

const StepDescription = styled.p`
  color: #64748b;
  line-height: 1.6;
  font-size: clamp(0.85rem, 1.1vw, 1.1rem);

  @media (min-width: 1920px) {
    font-size: clamp(1rem, 1.2vw, 1.2rem);
  }

  @media (min-width: 2560px) {
    font-size: clamp(1.1rem, 1.3vw, 1.3rem);
  }
`;

const StepConnector = styled.div`
  font-size: 1.5rem;
  color: #6366F1;
  font-weight: 600;

  @media (max-width: 768px) {
    transform: rotate(90deg);
  }
`;











function FAQSectionComponent() {
  const [openFAQIndex, setOpenFAQIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQIndex((prev) => (prev === index ? null : index));
  };

  return (
    <FAQSection>
      <FAQTitleWrapper>
        <SectionTitle>常见问题</SectionTitle>
        <DocumentLink
          href="https://my.feishu.cn/wiki/O0m4wLGihiedrNkBSLdcvhQ7ntO"
          target="_blank"
          rel="noopener noreferrer"
        >
          产品使用文档 <ExternalLink size={14} />
        </DocumentLink>
      </FAQTitleWrapper>
      <FAQContainer>
        {faqData.map((faq, index) => (
          <FAQItem key={index}>
            <FAQQuestion
              onClick={() => toggleFAQ(index)}
              isOpen={openFAQIndex === index}
            >
              <QuestionText>{faq.question}</QuestionText>
              <ChevronIcon isOpen={openFAQIndex === index}>
                <ChevronDown size={24} />
              </ChevronIcon>
            </FAQQuestion>
            <AnimatePresence initial={false}>
              {openFAQIndex === index && (
                <FAQAnswer
                  key={`faq-answer-${index}`}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                >
                  <AnswerInner>
                    <AnswerContent>{faq.answer}</AnswerContent>
                  </AnswerInner>
                </FAQAnswer>
              )}
            </AnimatePresence>
          </FAQItem>
        ))}
      </FAQContainer>
    </FAQSection>
  );
}

const FAQSection = styled.section`
  padding: 2rem 2rem 4rem;
  max-width: 900px;
  margin: 0 auto;
  background: #f8fafc;

  @media (min-width: 1920px) {
    max-width: 1100px;
    padding: 3rem 3rem 5rem;
  }

  @media (min-width: 2560px) {
    max-width: 1300px;
    padding: 4rem 4rem 6rem;
  }

  @media (max-width: 768px) {
    padding: 2rem 1rem 3rem;
  }
`;

const DocumentLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: #64748b;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: color 0.2s ease;
  white-space: nowrap;

  &:hover {
    color: #6366f1;
  }

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const HeroDocumentLink = styled(DocumentLink)`
  padding: 0.75rem 1.1rem;
  border-radius: 999px;
  border: 1px dashed rgba(129, 140, 248, 0.6);
  background: rgba(129, 140, 248, 0.06);
  color: #4f46e5;
  font-size: clamp(0.9rem, 1.1vw, 1rem);
  font-weight: 500;
  gap: 0.4rem;

  &:hover {
    color: #4338ca;
    background: rgba(129, 140, 248, 0.12);
    border-color: rgba(129, 140, 248, 0.9);
  }
`;

const FAQTitleWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 2rem;
  position: relative;

  ${SectionTitle} {
    text-align: center;
    margin-bottom: 0;
    grid-column: 2;
  }

  ${DocumentLink} {
    grid-column: 3;
    justify-self: end;
  }

  @media (min-width: 1920px) {
    margin-bottom: 3rem;
  }

  @media (min-width: 2560px) {
    margin-bottom: 4rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;

    ${SectionTitle} {
      grid-column: 1;
      text-align: center;
    }

    ${DocumentLink} {
      grid-column: 1;
      justify-self: center;
    }
  }
`;

const FAQContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: 1920px) {
    gap: 1.25rem;
  }

  @media (min-width: 2560px) {
    gap: 1.5rem;
  }
`;

const FAQItem = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  transition: box-shadow 0.3s ease, border-color 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #cbd5e1;
  }

  @media (min-width: 1920px) {
    border-radius: 16px;
  }

  @media (min-width: 2560px) {
    border-radius: 20px;
  }
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.3s ease;
  gap: 1rem;

  &:hover {
    background: #f8fafc;
  }

  @media (min-width: 1920px) {
    padding: 1.5rem 2rem;
  }

  @media (min-width: 2560px) {
    padding: 1.75rem 2.5rem;
  }
`;

const QuestionText = styled.h3`
  font-size: clamp(1rem, 1.3vw, 1.2rem);
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  flex: 1;
  line-height: 1.5;

  @media (min-width: 1920px) {
    font-size: clamp(1.2rem, 1.5vw, 1.4rem);
  }

  @media (min-width: 2560px) {
    font-size: clamp(1.3rem, 1.6vw, 1.5rem);
  }
`;

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: transform 0.3s ease, color 0.3s ease;
  flex-shrink: 0;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};

  ${FAQQuestion}:hover & {
    color: #6366F1;
  }
`;

const FAQAnswer = styled(motion.div)`
  overflow: hidden;
  background: white;
  will-change: height, opacity;
`;

const AnswerInner = styled.div`
  padding: 0 1.5rem 1.25rem;

  @media (min-width: 1920px) {
    padding: 0 2rem 1.5rem;
  }

  @media (min-width: 2560px) {
    padding: 0 2.5rem 1.75rem;
  }
`;

const AnswerContent = styled.p`
  font-size: clamp(0.9rem, 1.1vw, 1.1rem);
  line-height: 1.8;
  color: #64748b;
  margin: 0;
  padding-top: 0.5rem;

  @media (min-width: 1920px) {
    font-size: clamp(1rem, 1.2vw, 1.2rem);
    padding-top: 0.75rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(1.1rem, 1.3vw, 1.3rem);
    padding-top: 1rem;
  }
`;



const CompaniesSection = styled.section`
  padding: 0;
  margin-top: -2rem;
  text-align: center;
`;

const CompaniesTitle = styled.h2`
  font-size: clamp(2rem, 3.5vw, 3rem);
  font-weight: 700;
  margin-bottom: 1rem;
  color: #1a1a1a;
  padding: 0 2rem;

  @media (min-width: 1920px) {
    font-size: clamp(3rem, 3.5vw, 3.5rem);
    margin-bottom: 1.5rem;
    padding: 0 3rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(3.5rem, 4vw, 4rem);
    margin-bottom: 2rem;
    padding: 0 4rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 0.5rem;
    padding: 0 1rem;
  }
`;

const CompaniesContainer = styled.div`
  overflow: hidden;
  width: 100%;
  position: relative;
  margin-top: 0;
  
  &:before,
  &:after {
    content: "";
    position: absolute;
    top: 0;
    width: 120px;
    height: 100%;
    z-index: 2;
    pointer-events: none;
  }
  
  &:before {
    left: 0;
    background: linear-gradient(to right, #f8fafc, transparent);
  }
  
  &:after {
    right: 0;
    background: linear-gradient(to left, #f8fafc, transparent);
  }

  @media (max-width: 768px) {
    &:before,
    &:after {
      width: 70px;
    }
  }
`;

const CompaniesTrack = styled.div`
  display: flex;
  width: max-content;
  animation: scroll var(--scroll-duration, 40s) linear infinite;
  animation-play-state: ${props => props.$isAnimating ? 'running' : 'paused'};
  will-change: transform;
  gap: 0;
  padding: 1rem 0;
  
  @keyframes scroll {
    0% { transform: translate3d(0, 0, 0); }
    100% { transform: translate3d(calc(-1 * var(--scroll-distance, 0px)), 0, 0); }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: none;
  }
`;

const CompaniesGroup = styled.div`
  display: flex;
  gap: 2rem;
  padding-right: 2rem;

  @media (max-width: 768px) {
    gap: 1rem;
    padding-right: 1rem;
  }
`;

const CategoryBadge = styled.div`
  background: white;
  padding: 0.75rem 1.5rem;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 2px 4px -1px rgba(0, 0, 0, 0.03);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  flex-shrink: 0;
  min-width: max-content;

  @media (max-width: 768px) {
    padding: 0.625rem 1rem;
    gap: 0.5rem;
    border-radius: 12px;
  }
`;

const CategoryIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 26px;
    height: 26px;
  }

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    border-radius: 10px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const CategoryLabel = styled.span`
  font-weight: 600;
  color: #334155;
  font-size: 1rem;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;



const TestimonialsSection = styled.section`
  padding: 2rem 0 4rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  color: #1a1a1a;
  overflow: hidden;
  position: relative;
  
  /* 添加微妙的背景纹理 */
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");

  @media (min-width: 1920px) {
    padding: 3rem 0 6rem;
  }

  @media (min-width: 2560px) {
    padding: 4rem 0 8rem;
  }

  @media (max-width: 768px) {
    padding: 1.5rem 0 3rem;
  }
`;

const TestimonialsHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  padding: 0 2rem;
  position: relative;
  z-index: 3;

  @media (min-width: 1920px) {
    max-width: 1000px;
    margin: 0 auto 4rem;
    padding: 0 3rem;
  }

  @media (min-width: 2560px) {
    max-width: 1200px;
    margin: 0 auto 5rem;
    padding: 0 4rem;
  }
`;

const TestimonialsTitle = styled.h2`
  font-size: clamp(2rem, 3.5vw, 3rem);
  font-weight: 700;
  margin-bottom: 1rem;
  color: #1a1a1a;

  @media (min-width: 1920px) {
    font-size: clamp(3rem, 3.5vw, 3.5rem);
    margin-bottom: 1.5rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(3.5rem, 4vw, 4rem);
    margin-bottom: 2rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const TestimonialsSubtitle = styled.p`
  font-size: clamp(1rem, 1.5vw, 1.4rem);
  line-height: 1.6;
  margin-bottom: 2rem;
  color: #64748b;

  @media (min-width: 1920px) {
    font-size: clamp(1.3rem, 1.8vw, 1.6rem);
    margin-bottom: 2.5rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(1.5rem, 2vw, 1.8rem);
    margin-bottom: 3rem;
  }
`;

const TestimonialsGrid = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  position: relative;
  overflow: hidden;

  @media (min-width: 1920px) {
    max-width: 1600px;
  }

  @media (min-width: 2560px) {
    max-width: 1800px;
  }
`;

const TestimonialsTrack = styled.div`
  display: grid;
  grid-template-rows: repeat(2, auto);
  gap: 1.5rem;
  padding: 0 2rem;
  position: relative;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 150px;
    z-index: 2;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, 
      #f8fafc 0%, 
      rgba(248, 250, 252, 0.9) 20%, 
      rgba(248, 250, 252, 0) 100%
    );
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, 
      #f8fafc 0%, 
      rgba(248, 250, 252, 0.9) 20%, 
      rgba(248, 250, 252, 0) 100%
    );
  }
  
  @media (max-width: 768px) {
    grid-template-rows: auto;
    padding: 0 1rem;

    &::before,
    &::after {
      width: 80px;
    }
  }
`;

const TestimonialsRow = styled.div`
  display: flex;
  gap: 1.5rem;
  animation: scrollLeft 60s linear infinite;
  will-change: transform;
  padding: 1rem 0;
  position: relative;

  &:nth-child(2) {
    animation-direction: reverse;
    animation-duration: 55s;
  }

  @keyframes scrollLeft {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-50%);
    }
  }
`;

const TestimonialCard = styled.div`
  flex: 0 0 420px;
  height: 240px;
  margin: 0 12px;
  padding: 28px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  
  /* 添加精致的边框装饰 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #06b6d4 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  /* 添加微妙的背景图案 */
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.03) 0%, transparent 70%);
    transform: rotate(45deg);
    transition: all 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-6px) scale(1.02);
    box-shadow: 
      0 16px 20px -5px rgba(0, 0, 0, 0.1),
      0 8px 8px -5px rgba(0, 0, 0, 0.04),
      0 0 0 1px rgba(99, 102, 241, 0.1);
    border-color: rgba(99, 102, 241, 0.2);
    background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
    
    &::before {
      opacity: 1;
    }
    
    &::after {
      transform: rotate(45deg) scale(1.5);
      opacity: 0.08;
    }
  }
  
  @media (max-width: 768px) {
    flex: 0 0 320px;
    margin: 0 8px;
    padding: 20px 16px;
  }
`;

const TestimonialContent = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #1e293b;
  margin-bottom: 24px;
  font-weight: 500;
  position: relative;
  z-index: 1;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
  flex: 1;
  display: flex;
  align-items: flex-start;
  
  /* 添加引号装饰 */
  &::before {
    content: '"';
    position: absolute;
    top: -12px;
    left: -8px;
    font-size: 48px;
    color: rgba(99, 102, 241, 0.15);
    font-family: Georgia, serif;
    line-height: 1;
    z-index: -1;
  }
  
  &::after {
    content: '"';
    position: absolute;
    bottom: -36px;
    right: -8px;
    font-size: 48px;
    color: rgba(99, 102, 241, 0.15);
    font-family: Georgia, serif;
    line-height: 1;
    z-index: -1;
  }
  
  ${TestimonialCard}:hover & {
    color: #0f172a;
    text-shadow: 0 1px 2px rgba(255, 255, 255, 0.9);
  }
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  z-index: 1;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
  background: linear-gradient(to bottom, transparent, rgba(248, 250, 252, 0.8));
  transition: all 0.3s ease;
  flex-shrink: 0;
  margin-top: auto;
  
  &:hover {
    border-top-color: #cbd5e1;
    background: linear-gradient(to bottom, transparent, rgba(241, 245, 249, 0.9));
  }
`;

const AuthorAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: #ffffff;
  border: 2px solid #e2e8f0;
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: contrast(1.05) brightness(1.05);
  }
  
  &:hover {
    border-color: #6366f1;
    box-shadow: 
      0 8px 15px -3px rgba(99, 102, 241, 0.3),
      0 4px 6px -2px rgba(0, 0, 0, 0.1);
    transform: scale(1.1);
  }
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AuthorName = styled.span`
  font-weight: 700;
  color: #1e293b;
  font-size: 16px;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
  transition: color 0.3s ease;
  
  ${TestimonialCard}:hover & {
    color: #6366f1;
  }
`;

const AuthorTitle = styled.span`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
  transition: color 0.3s ease;
  
  ${TestimonialCard}:hover & {
    color: #7c3aed;
  }
`;

// 更新testimonials数据
const testimonials = [
  {
    content: "之前每次技术面都被问到细节就卡壳，明明有项目却讲不清。用职唛AI做了两周针对字节岗位的模拟面试和复盘，搞清楚评分标准后，回答结构顺了很多，现在面对技术面不再发怵，能按面试官思路清晰拆解问题。",
    author: "张先生",
    title: "前端开发工程师, 字节跳动",
    avatar: "/images/avatars/Zhang_zijie.png"
  },
  {
    content: "投了很多简历都石沉大海，我完全不知道 HR 在看什么。按职唛AI的简历诊断逐条改完，再用题库按岗位刷题，一个月内明显感觉面试机会多了起来，通过率也稳定了不少。",
    author: "李女士",
    title: "算法工程师, 阿里巴巴",
    avatar: "/images/avatars/Li_alibaba.png"
  },
  {
    content: "应届生最大的问题就是没经验、没故事。职唛AI帮我把实习项目拆成 STAR 故事，还针对产品岗高频题反复演练，从一开始紧张结巴，到最后在腾讯终面也能全程自信输出，逻辑和故事性都提升了很多。",
    author: "王先生",
    title: "产品经理, 腾讯",
    avatar: "/images/avatars/Wang_tengxun.png"
  },
  {
    content: "以前一到作品集讲解就超时、答非所问，美团那种多轮面试压力特别大。通过职唛AI反复练习作品讲解和追问场景，系统会即时指出啰嗦和逻辑断点，现在不管几轮面试，我都能在规定时间内把重点讲清楚。",
    author: "刘女士",
    title: "UI设计师, 美团",
    avatar: "/images/avatars/Liu_meituan.png"
  },
  {
    content: "我一直以为简历写得还行，直到职唛AI提示我和 JD 的关键词匹配度很低。按建议补充了项目细节和技术指标，再配合岗位押题模拟，后面去面京东等几家公司时，技术亮点都能一下讲明白，面试反馈也明显更积极。",
    author: "陈先生",
    title: "后端工程师, 京东",
    avatar: "/images/avatars/Chen_jingdong.png"
  },
  {
    content: "之前面试总被问职业规划，我自己都说不清。用职唛AI梳理经历、匹配岗位后，很快锁定了更适合我的游戏策划方向，现在每次面试都能把“为什么选这个方向”讲得既真诚又有逻辑，不再盲目投递。",
    author: "赵女士",
    title: "游戏策划, 网易",
    avatar: "/images/avatars/Zhao_wangyi.png"
  },
  {
    content: "我以前最怕“自我介绍”，总是说着说着就跑题，要么太流水账，要么听起来像背稿子。通过职唛AI的模拟面试来回练了不下 20 遍，一步步指导我怎么进行更合适的自我介绍。现在每次正式面试，一开口就能自然地把亮点抛出去，后面的技术问答节奏也顺多了。",
    author: "周先生",
    title: "高级工程师, 快手",
    avatar: "/images/avatars/Zhou_kuaishou.png"
  },
  {
    content: "转岗做产品运营前，我既怕表达不专业，又怕被问穿底细。职唛AI根据我的过往经历自动生成可讲的案例，并针对运营岗位高频问题做多轮练习，现在在小米的日常沟通和复盘中，我也能用更“运营思维”的方式表达自己。",
    author: "林女士",
    title: "产品运营, 小米",
    avatar: "/images/avatars/Lin_xiaomi.png"
  },
  {
    content: "算法和系统设计一直是我的短板，每次都答得很散。职唛AI按岗位拆分知识点、生成高质量追问，还会给示范回答结构，刷了几轮之后，百度技术面的深度提问也能稳住了。",
    author: "黄先生",
    title: "机器学习工程师, 百度",
    avatar: "/images/avatars/Huang_baidu.png"
  },
  {
    content: "以前在面试和项目答辩时，总担心遗漏关键问题、回答不够专业。现在用会议助手实时捕捉问题，再结合智能回答建议整理话术，不论是滴滴的技术面试还是团队评审，我都能清晰、有条理地回应。",
    author: "杨女士",
    title: "技术经理, 滴滴",
    avatar: "/images/avatars/Yang_didi.png"
  }
];

// FAQ数据
const faqData = [
  {
    question: "支持哪些类型的面试准备？",
    answer: "职唛AI提供全流程面试准备服务，包括：1) 简历诊断 - AI深度分析简历，提供优化建议；2) 面试押题 - 基于岗位JD生成针对性面试题库；3) 模拟面试 - 1对1真实模拟面试，AI实时反馈；4) 会议助手 - 实时记录会议对话，自动识别对话中的【问题】并提供个性化智能回答建议。无论是技术面试、行为面试还是系统设计，都能为你提供专业指导。"
  },
  {
    question: "模拟面试是如何进行的？",
    answer: "模拟面试采用实时语音对话方式，AI面试官会根据你选择的岗位和简历内容，提出真实的面试问题。你可以通过语音和AI面试官进行对话，系统会实时分析你的表现，并在结束后给出详细的评估报告和改进建议。整个过程完全模拟真实面试场景，帮助你提前适应面试节奏。"
  },
  {
    question: "会议助手能否识别腾讯会议、Zoom等平台的声音？",
    answer: "可以。会议助手通过浏览器麦克风收音，支持识别腾讯会议、Zoom、飞书等主流会议平台的声音。使用时需要打开扬声器播放会议声音，让麦克风能够采集到，以确保功能正常使用。"
  },
  {
    question: "简历诊断的准确性如何？",
    answer: "职唛AI的简历诊断基于大语言模型和行业知识库，能够从多个维度分析简历：可搜索性、技能匹配度、招聘官视角、行业匹配度等。系统会提供详细的诊断报告和具体的优化建议，帮助你的简历在众多求职者中脱颖而出。"
  },
  {
    question: "能否结合我的个人资料来回答问题？",
    answer: "支持。你可以在个人资料中填写工作经历、项目经验、技能特长等信息，模拟面试和会议助手在生成问题、给出回答建议时都会结合这些资料，让内容更贴合你的背景。例如会优先围绕你熟悉的技术栈和项目提问，并给出更个性化的表达建议，从而显著提升面试和会议中的表现。"
  },
  {
    question: "都支持哪些语言？",
    answer: "当前支持中文、英文、日语、韩语四种语言的模拟面试和会议助手。你可以在创建会话或模拟面试时选择对应语言，系统会以该语言进行语音识别、问题生成和回答建议。后续我们也会根据用户需求逐步扩展更多语种。"
  },
  // 如后续有更多高频问题，可在此继续追加
];

// 修改TestimonialsSectionContent组件
const TestimonialsSectionContent = () => {
  // 将testimonials分成两组，每组5个
  const row1 = testimonials.slice(0, 5);
  const row2 = testimonials.slice(5);

  return (
    <TestimonialsGrid>
      <TestimonialsTrack>
        <TestimonialsRow>
          {[...row1, ...row1].map((testimonial, index) => (
            <TestimonialCard key={`row1-${index}`}>
              <TestimonialContent>
                {testimonial.content}
              </TestimonialContent>
              <TestimonialAuthor>
                <AuthorAvatar>
                  <img
                    src={testimonial.avatar}
                    alt={`${testimonial.author}的头像`}
                    loading="lazy"
                    decoding="async"
                    fetchpriority="low"
                  />
                </AuthorAvatar>
                <AuthorInfo>
                  <AuthorName>{testimonial.author}</AuthorName>
                  <AuthorTitle>{testimonial.title}</AuthorTitle>
                </AuthorInfo>
              </TestimonialAuthor>
            </TestimonialCard>
          ))}
        </TestimonialsRow>
        <TestimonialsRow>
          {[...row2, ...row2].map((testimonial, index) => (
            <TestimonialCard key={`row2-${index}`}>
              <TestimonialContent>
                {testimonial.content}
              </TestimonialContent>
              <TestimonialAuthor>
                <AuthorAvatar>
                  <img
                    src={testimonial.avatar}
                    alt={`${testimonial.author}的头像`}
                    loading="lazy"
                    decoding="async"
                    fetchpriority="low"
                  />
                </AuthorAvatar>
                <AuthorInfo>
                  <AuthorName>{testimonial.author}</AuthorName>
                  <AuthorTitle>{testimonial.title}</AuthorTitle>
                </AuthorInfo>
              </TestimonialAuthor>
            </TestimonialCard>
          ))}
        </TestimonialsRow>
      </TestimonialsTrack>
    </TestimonialsGrid>
  );
};

export default HomePage;
